/**
 * HYPSEYA — My Meditations
 * Standalone full-page meditation library with player, filters and detail panel.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import ConfirmModal from '@/components/ConfirmModal';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Meditation scripts carry audio-direction tags for synthesis — [whispers],
// [PAUSE 5s], and similar. Strip them so the reader sees only the words of the
// meditation. The raw script is kept untouched for re-synthesis.
const cleanScriptForDisplay = (raw = '') =>
  raw
    .replace(/\[[^\]]*\]/g, ' ')        // drop [whispers], [PAUSE 5s], any [tag]
    .replace(/\s+([,.;:!?])/g, '$1')    // no space before punctuation
    .replace(/[ \t]{2,}/g, ' ')          // collapse runs of spaces
    .replace(/[ \t]+\n/g, '\n')          // trailing spaces per line
    .replace(/\n{3,}/g, '\n\n')          // collapse excess blank lines
    .trim();

const STATUS_CONFIG = {
  draft:        { label: 'Draft',        color: '#F0A500' },
  script_ready: { label: 'Script Ready', color: '#2D8B87' },
  audio_ready:  { label: 'Ready',        color: '#1E6B68' },
};

const FOCUS_LABELS = {
  anxiety: 'Anxiety', sleep: 'Sleep', confidence: 'Confidence',
  grief: 'Grief', gratitude: 'Gratitude', stress: 'Stress',
  self_love: 'Self Love', morning: 'Morning', recovery: 'Recovery',
  regulation: 'Regulation', custom: 'Custom',
};

function formatTime(s) {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
}

// ── Empty state ─────────────────────────────────────────────────────────────
function EmptyState({ hasFilters, onClear, onNavigate }) {
  return (
    <div style={{
      padding: '60px 24px', textAlign: 'center',
      background: 'rgba(255,255,255,0.88)', borderRadius: '20px',
      border: '1px solid rgba(184,211,207,0.3)',
    }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '16px', opacity: 0.4 }}>♪</div>
      {hasFilters ? (
        <>
          <p style={{ color: '#8FA3A0', fontSize: '0.95rem', margin: '0 0 16px' }}>
            No meditations match these filters.
          </p>
          <button
            onClick={onClear}
            style={{
              padding: '10px 24px', background: 'transparent', color: '#1E6B68',
              border: '1.5px solid rgba(30,107,104,0.3)', borderRadius: '20px',
              fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            Clear filters
          </button>
        </>
      ) : (
        <>
          <p style={{ color: '#8FA3A0', fontSize: '0.95rem', margin: '0 0 6px' }}>
            No meditations yet.
          </p>
          <p style={{ color: '#B8C8C6', fontSize: '0.82rem', margin: '0 0 20px' }}>
            Go to Studio → Create Meditation to build your first one.
          </p>
          <button
            onClick={onNavigate}
            style={{
              padding: '12px 28px',
              background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              color: '#fff', border: 'none', borderRadius: '20px',
              fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Create a meditation →
          </button>
        </>
      )}
    </div>
  );
}

// ── Audio Player ────────────────────────────────────────────────────────────
function AudioPlayer({ src, title }) {
  const audioRef = useRef(new Audio());
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    const onTime     = () => setCurrentTime(audio.currentTime);
    const onDuration = () => setDuration(audio.duration || 0);
    const onEnded    = () => { setIsPlaying(false); setCurrentTime(0); };
    audio.addEventListener('timeupdate',    onTime);
    audio.addEventListener('durationchange', onDuration);
    audio.addEventListener('ended',          onEnded);
    return () => {
      audio.removeEventListener('timeupdate',    onTime);
      audio.removeEventListener('durationchange', onDuration);
      audio.removeEventListener('ended',          onEnded);
      audio.pause();
    };
  }, []);

  // New src → reload
  useEffect(() => {
    const audio = audioRef.current;
    audio.pause();
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (src) audio.src = src;
  }, [src]);

  const toggle = () => {
    const audio = audioRef.current;
    if (isPlaying) { audio.pause(); setIsPlaying(false); }
    else           { audio.play(); setIsPlaying(true); }
  };

  const seek = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  if (!src) return null;

  return (
    <div style={{
      padding: '20px', marginBottom: '20px',
      background: 'linear-gradient(135deg, rgba(30,107,104,0.06) 0%, rgba(184,211,207,0.15) 100%)',
      borderRadius: '14px', border: '1px solid rgba(30,107,104,0.1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          onClick={toggle}
          style={{
            width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
            color: '#fff', border: 'none', fontSize: '1.2rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(30,107,104,0.3)',
          }}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <div style={{ flex: 1 }}>
          <div
            onClick={seek}
            style={{
              height: 6, background: 'rgba(30,107,104,0.15)',
              borderRadius: 3, cursor: 'pointer', marginBottom: 6, position: 'relative',
            }}
          >
            <div style={{
              height: '100%',
              width: duration ? `${(currentTime / duration) * 100}%` : '0%',
              background: 'linear-gradient(90deg, #1E6B68, #2D8B87)',
              borderRadius: 3, transition: 'width 0.1s linear',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#999' }}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Detail Panel ────────────────────────────────────────────────────────────
function DetailPanel({ med, userId, onClose, onDeleted, onRefresh }) {
  const navigate = useNavigate();
  const [showScript, setShowScript] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [isResynthesizing, setIsResynthesizing] = useState(false);
  const sc = STATUS_CONFIG[med.status] || { label: med.status, color: '#999' };
  const audioSrc = med.status === 'audio_ready'
    ? `${API}/meditations/${med.id}/audio?user_id=${userId}`
    : null;

  const handleDelete = () => {
    setConfirmModal({
      title: 'Remove this meditation?',
      message: 'This cannot be undone.',
      confirmLabel: 'Remove',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/meditations/${med.id}`, { params: { user_id: userId } });
          toast.success('Meditation removed');
          onDeleted(med.id);
          onClose();
        } catch {
          toast.error('Could not remove meditation');
        }
      },
    });
  };

  const handleDuplicate = async () => {
    try {
      await axios.post(`${API}/meditations/${med.id}/duplicate`, null, { params: { user_id: userId } });
      toast.success('Meditation duplicated');
      onRefresh();
    } catch {
      toast.error('Could not duplicate meditation');
    }
  };

  const handleResynthesize = async () => {
    setIsResynthesizing(true);
    try {
      await axios.post(`${API}/meditations/${med.id}/synthesize`, null, { params: { user_id: userId } });
      toast.success('Audio re-generated');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not re-generate audio');
    } finally {
      setIsResynthesizing(false);
    }
  };

  const handleEdit = () => {
    navigate('/studio', { state: { tab: 'create', editMeditationId: med.id } });
  };

  return (
    <>
    {confirmModal && <ConfirmModal {...confirmModal} onClose={() => setConfirmModal(null)} />}
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900,
      background: 'rgba(10,30,28,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'flex-end',
      animation: 'hyp-fade-in 0.2s ease',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 680, margin: '0 auto',
        background: '#FAFBF8', borderRadius: '24px 24px 0 0',
        padding: '28px 24px 40px',
        maxHeight: '88vh', overflowY: 'auto',
        animation: 'hyp-slide-up 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        {/* Handle */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D8E5E3', margin: '0 auto 20px' }} />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <h3 style={{
              margin: '0 0 10px', color: '#1E6B68',
              fontFamily: "'Playfair Display', serif", fontSize: '1.35rem', lineHeight: 1.2,
            }}>
              {med.title}
            </h3>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ padding: '3px 10px', background: sc.color, color: '#fff', borderRadius: 20, fontSize: '0.68rem', fontWeight: 600 }}>
                {sc.label}
              </span>
              <span style={{ padding: '3px 10px', background: 'rgba(30,107,104,0.1)', color: '#1E6B68', borderRadius: 20, fontSize: '0.68rem' }}>
                {FOCUS_LABELS[med.focus] || med.focus}
              </span>
              <span style={{ padding: '3px 10px', background: 'rgba(184,211,207,0.35)', color: '#2D8B87', borderRadius: 20, fontSize: '0.68rem' }}>
                {med.length_minutes} min
              </span>
              {med.tone && (
                <span style={{ padding: '3px 10px', background: 'rgba(184,211,207,0.2)', color: '#2D8B87', borderRadius: 20, fontSize: '0.68rem' }}>
                  {med.tone}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#ccc', cursor: 'pointer', lineHeight: 1, padding: '2px', flexShrink: 0 }}>×</button>
        </div>

        {/* Player */}
        {audioSrc && <AudioPlayer key={med.id} src={audioSrc} title={med.title} />}

        {/* No audio yet — prompt */}
        {med.status === 'script_ready' && (
          <div style={{
            padding: '16px 18px', marginBottom: 20,
            background: 'rgba(240,165,0,0.06)', border: '1px solid rgba(240,165,0,0.2)',
            borderRadius: 12,
          }}>
            <p style={{ margin: 0, fontSize: '0.84rem', color: '#B8860B' }}>
              Script ready. Re-generate audio to listen — make sure your Voice Lab is set up first.
            </p>
          </div>
        )}

        {/* Script */}
        {med.script && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0, color: '#1E6B68', fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Script</h4>
              <button
                onClick={() => setShowScript(s => !s)}
                style={{ background: 'none', border: 'none', color: '#2D8B87', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {showScript ? 'Collapse' : 'Read full script'}
              </button>
            </div>
            <div style={{
              padding: '14px 16px', background: 'rgba(184,211,207,0.08)',
              borderRadius: 10, maxHeight: showScript ? 360 : 90, overflow: 'auto',
              fontFamily: "'Georgia', serif", lineHeight: 1.8,
              color: '#666', fontSize: '0.88rem', transition: 'max-height 0.3s ease',
              whiteSpace: 'pre-wrap',
            }}>
              {cleanScriptForDisplay(med.script)}
            </div>
          </div>
        )}

        {/* Created */}
        <p style={{ margin: '0 0 20px', color: '#AABAB8', fontSize: '0.72rem' }}>
          Created {new Date(med.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {med.script && (
            <button
              onClick={handleResynthesize}
              disabled={isResynthesizing}
              style={{
                padding: '10px 18px', background: 'rgba(30,107,104,0.07)', color: '#1E6B68',
                border: '1.5px solid rgba(30,107,104,0.25)', borderRadius: 20,
                cursor: isResynthesizing ? 'not-allowed' : 'pointer',
                fontSize: '0.84rem', opacity: isResynthesizing ? 0.6 : 1,
              }}
            >
              {isResynthesizing ? 'Generating…' : '↺ Re-generate audio'}
            </button>
          )}
          <button
            onClick={handleDuplicate}
            style={{
              padding: '10px 18px', background: 'rgba(30,107,104,0.07)', color: '#1E6B68',
              border: '1.5px solid rgba(30,107,104,0.25)', borderRadius: 20,
              cursor: 'pointer', fontSize: '0.84rem',
            }}
          >
            ⎘ Duplicate
          </button>
          <button
            onClick={handleEdit}
            style={{
              padding: '10px 18px', background: 'rgba(30,107,104,0.07)', color: '#1E6B68',
              border: '1.5px solid rgba(30,107,104,0.25)', borderRadius: 20,
              cursor: 'pointer', fontSize: '0.84rem',
            }}
          >
            ✏ Edit
          </button>
          <button
            onClick={handleDelete}
            style={{
              padding: '10px 18px', background: 'rgba(192,57,43,0.05)', color: '#C0392B',
              border: '1px solid rgba(192,57,43,0.2)', borderRadius: 20,
              cursor: 'pointer', fontSize: '0.84rem',
            }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function MyMeditations() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth0();
  const [user, setUser] = useState(null);
  const [meditations, setMeditations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFocus, setFilterFocus] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Load user from localStorage (set by Auth0 sync in App.js)
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) { try { setUser(JSON.parse(stored)); } catch {} }
  }, []);

  useEffect(() => {
    if (user?.user_id) loadMeditations();
  }, [user, filterStatus, filterFocus]);

  const loadMeditations = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterFocus)  params.append('focus',  filterFocus);
      const res = await axios.get(`${API}/meditations/${user.user_id}?${params}`);
      setMeditations(res.data);
    } catch {
      toast.error('Could not load meditations');
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterStatus('');
    setFilterFocus('');
    setSearchQuery('');
    setSortBy('newest');
  };

  // Filter + sort
  let filtered = meditations.filter(m =>
    !searchQuery || m.title.toLowerCase().includes(searchQuery.toLowerCase())
  );
  if (sortBy === 'oldest')  filtered = [...filtered].reverse();
  if (sortBy === 'alpha')   filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));
  if (sortBy === 'ready')   filtered = [...filtered].sort((a, b) => (a.status === 'audio_ready' ? -1 : 1));

  const hasFilters = !!(searchQuery || filterStatus || filterFocus);
  const ctrl = {
    padding: '10px 14px', borderRadius: '10px',
    border: '1.5px solid rgba(30,107,104,0.2)',
    fontSize: '0.86rem', background: 'rgba(255,255,255,0.92)',
    color: '#333', outline: 'none', cursor: 'pointer',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F7F3EC 0%, #EFE7DD 50%, rgba(184,211,207,0.25) 100%)',
      padding: '0 0 100px',
    }}>
      {/* Header */}
      <div style={{
        padding: '28px 24px 0',
        maxWidth: 700, margin: '0 auto',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', color: '#8FA3A0',
            fontSize: '0.82rem', cursor: 'pointer', padding: 0,
            marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          ← Back
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
          <div>
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.7rem', color: '#1E3A39',
              margin: '0 0 4px', fontWeight: 500,
            }}>My Meditations</h1>
            <p style={{ margin: 0, color: '#8FA3A0', fontSize: '0.82rem' }}>
              {isLoading ? '…' : `${meditations.length} meditation${meditations.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={() => navigate('/studio', { state: { tab: 'create' } })}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              color: '#fff', border: 'none', borderRadius: 20,
              fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 3px 12px rgba(30,107,104,0.25)',
            }}
          >
            + New
          </button>
        </div>

        {/* Search + filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search meditations…"
            style={{ ...ctrl, flex: '1 1 160px', minWidth: 160 }}
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...ctrl, flex: '0 0 auto' }}>
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="script_ready">Script Ready</option>
            <option value="audio_ready">Ready</option>
          </select>
          <select value={filterFocus} onChange={e => setFilterFocus(e.target.value)} style={{ ...ctrl, flex: '0 0 auto' }}>
            <option value="">All Focus</option>
            {Object.entries(FOCUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...ctrl, flex: '0 0 auto' }}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="alpha">A → Z</option>
            <option value="ready">Ready first</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '0 24px' }}>

        {/* Loading */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#2D8B87', fontSize: '0.9rem' }}>
            Loading your meditations…
          </div>
        )}

        {/* Empty */}
        {!isLoading && filtered.length === 0 && (
          <EmptyState
            hasFilters={hasFilters}
            onClear={clearFilters}
            onNavigate={() => navigate('/studio', { state: { tab: 'create' } })}
          />
        )}

        {/* Grid */}
        {!isLoading && filtered.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 12,
          }}>
            {filtered.map(med => {
              const sc = STATUS_CONFIG[med.status] || { label: med.status, color: '#999' };
              return (
                <button
                  key={med.id}
                  onClick={() => setSelected(med)}
                  style={{
                    padding: '18px 20px', textAlign: 'left',
                    background: 'rgba(255,255,255,0.94)',
                    borderRadius: 16, cursor: 'pointer',
                    border: '1.5px solid rgba(30,107,104,0.1)',
                    boxShadow: '0 2px 12px rgba(30,107,104,0.06)',
                    transition: 'all 0.18s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(30,107,104,0.14)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(30,107,104,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {/* Status badge + play icon */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                    <span style={{
                      padding: '3px 9px', background: sc.color, color: '#fff',
                      borderRadius: 20, fontSize: '0.64rem', fontWeight: 600, flexShrink: 0,
                    }}>
                      {sc.label}
                    </span>
                    {med.status === 'audio_ready' && (
                      <span style={{ fontSize: '1rem', opacity: 0.5 }}>♪</span>
                    )}
                  </div>

                  {/* Title */}
                  <h4 style={{
                    margin: '0 0 8px', color: '#1E3A39',
                    fontSize: '0.96rem', fontWeight: 600, lineHeight: 1.3,
                  }}>
                    {med.title}
                  </h4>

                  {/* Tags */}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
                    <span style={{ padding: '2px 8px', background: 'rgba(30,107,104,0.08)', color: '#1E6B68', borderRadius: 20, fontSize: '0.7rem' }}>
                      {FOCUS_LABELS[med.focus] || med.focus}
                    </span>
                    <span style={{ padding: '2px 8px', background: 'rgba(184,211,207,0.35)', color: '#2D8B87', borderRadius: 20, fontSize: '0.7rem' }}>
                      {med.length_minutes} min
                    </span>
                  </div>

                  <p style={{ margin: 0, color: '#C4D0CE', fontSize: '0.7rem' }}>
                    {new Date(med.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <DetailPanel
          med={selected}
          userId={user?.user_id}
          onClose={() => setSelected(null)}
          onDeleted={id => setMeditations(prev => prev.filter(m => m.id !== id))}
          onRefresh={loadMeditations}
        />
      )}

      <style>{`
        @keyframes hyp-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes hyp-slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
